import React from "react";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import { TruncatedText } from "components/common/TruncatedText";

export const LINK_GRID_COLUMNS = "minmax(0, 1fr) auto minmax(0, 1fr)";

export interface LinkColumnPair {
  first: string;
  second: string;
}

interface LinkColumnPairsProps {
  pairs: LinkColumnPair[];
  columnClassName?: string;
  secondPrefix?: string;
  className?: string;
  /** Membership link (`value ∈ list`): renders "∈" instead of the swap
   *  icon so the direction is readable off the row. */
  membership?: boolean;
}

export const LinkColumnPairs: React.FC<LinkColumnPairsProps> = ({
  pairs,
  columnClassName = "text-fg-muted-regular",
  secondPrefix,
  className,
  membership = false,
}) => (
  <div
    className={`grid items-center gap-x-2 gap-y-0.5 min-w-0 ${className ?? ""}`}
    style={{ gridTemplateColumns: LINK_GRID_COLUMNS }}
  >
    <div className="flex flex-col gap-0.5 min-w-0">
      {pairs.map((p, i) => (
        <TruncatedText
          key={`first-${p.first}-${i}`}
          className={`min-w-0 ${columnClassName}`}
        >
          {p.first}
        </TruncatedText>
      ))}
    </div>

    {membership ? (
      <span
        className="text-accent shrink-0 self-center justify-self-center leading-none"
        title="Membership: the value occurs in the list"
        aria-label="contains"
      >
        ∈
      </span>
    ) : (
      <SwapHorizOutlinedIcon className="icon-sm text-accent shrink-0 self-center justify-self-center" />
    )}

    <div className="flex flex-col gap-0.5 min-w-0">
      {pairs.map((p, i) => (
        <div
          key={`second-${p.second}-${i}`}
          className="flex items-center gap-1 min-w-0"
        >
          {secondPrefix && i === 0 && (
            <TruncatedText className="text-fg-medium min-w-0">
              {`${secondPrefix}.`}
            </TruncatedText>
          )}
          <TruncatedText className={`min-w-0 ${columnClassName}`}>
            {p.second}
          </TruncatedText>
        </div>
      ))}
    </div>
  </div>
);

interface LinkTablesHeaderProps {
  /** Leading content for each side (icon/dot + table name). */
  first: React.ReactNode;
  second: React.ReactNode;
  className?: string;
}

export const LinkTablesHeader: React.FC<LinkTablesHeaderProps> = ({
  first,
  second,
  className,
}) => (
  <div
    className={`grid items-center gap-x-2 min-w-0 ${className ?? ""}`}
    style={{ gridTemplateColumns: LINK_GRID_COLUMNS }}
  >
    <div className="flex items-center gap-1 min-w-0">{first}</div>
    <SwapHorizOutlinedIcon className="icon-sm invisible justify-self-center" />
    <div className="flex items-center gap-1 min-w-0">{second}</div>
  </div>
);
